import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import LoadingState from './ui/LoadingState';
import { PaymentStatusChip } from './ui/StatusChip';
import api from '../api';
import tokens from '../theme/tokens';
import { formatCurrency } from '../utils/image';

const POLL_INTERVAL = 5000;

function maskCpf(value) {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/**
 * Pagamento PIX de uma cobrança (pelo token do link): mostra valor, gera o QR Code/copia e cola
 * e acompanha a confirmação. Usado na página /pagamento/:token e no aviso de mensalidade em aberto.
 * `autoGerar` gera o PIX assim que carregar (quando o CPF já está no cadastro).
 * `acaoPago` é renderizado quando o pagamento é confirmado; `onStatus(status)` avisa mudanças.
 */
const PixCheckout = ({ token, autoGerar = false, acaoPago, onLoad, onStatus }) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [cpf, setCpf] = useState('');
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);
  const autoGerado = useRef(false);

  const atualizar = useCallback((dados) => {
    setPayment(dados);
    onLoad && onLoad(dados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generatePix = useCallback(async (atual) => {
    setError('');
    setGenerating(true);
    try {
      const body = atual.hasCpf && !cpf ? {} : { cpf };
      const response = await api.post(`pagamentos/link/${token}/pix`, body);
      atualizar(response.data);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'CPF_REQUIRED') setPayment((current) => ({ ...current, hasCpf: false }));
      setError(data.error || 'Não foi possível gerar o PIX.');
    } finally {
      setGenerating(false);
    }
  }, [token, cpf, atualizar]);

  useEffect(() => {
    setLoading(true);
    autoGerado.current = false;
    api
      .get(`pagamentos/link/${token}`)
      .then((response) => atualizar(response.data))
      .catch((err) => setError(err.response?.data?.error || 'Não foi possível carregar o pagamento.'))
      .finally(() => setLoading(false));
  }, [token, atualizar]);

  // Gera o PIX automaticamente quando pedido e o CPF já está cadastrado
  useEffect(() => {
    if (autoGerar && payment && payment.status === 'pending' && !payment.pix && payment.hasCpf && !autoGerado.current) {
      autoGerado.current = true;
      generatePix(payment);
    }
  }, [autoGerar, payment, generatePix]);

  // Enquanto houver PIX na tela, consulta o status até a confirmação via webhook.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (payment && payment.status === 'pending' && payment.pix) {
      pollRef.current = setInterval(async () => {
        try {
          const response = await api.get(`pagamentos/link/${token}/status`);
          if (response.data.status !== 'pending') {
            setPayment((current) => ({ ...current, status: response.data.status, paidAt: response.data.paidAt }));
            onStatus && onStatus(response.data.status);
          }
        } catch (err) {
          // tenta de novo no próximo ciclo
        }
      }, POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [payment, token, onStatus]);

  // Apenas no ambiente de desenvolvimento (provedor "simulado").
  const simularPagamento = async () => {
    try {
      const response = await api.post(`pagamentos/link/${token}/simular`);
      setPayment((current) => ({ ...current, status: response.data.status, paidAt: response.data.paidAt }));
      onStatus && onStatus(response.data.status);
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível simular o pagamento.');
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(payment.pix.copiaECola);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      setError('Copie o código manualmente.');
    }
  };

  const renderBody = () => {
    if (loading) return <LoadingState label="Carregando pagamento..." minHeight={200} />;
    if (!payment) return <Alert severity="error">{error}</Alert>;

    if (payment.status === 'paid') {
      return (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 3, textAlign: 'center' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 72, color: 'success.main' }} />
          <Typography variant="h5">Pagamento confirmado!</Typography>
          <Typography color="text.secondary">
            O valor já foi lançado no seu extrato{payment.paidAt ? ` em ${new Date(payment.paidAt).toLocaleString('pt-BR')}` : ''}.
          </Typography>
          {acaoPago}
        </Stack>
      );
    }

    if (payment.status !== 'pending') {
      return <Alert severity="info">Esta cobrança não está mais disponível para pagamento.</Alert>;
    }

    if (payment.pix) {
      return (
        <Stack spacing={2.5} alignItems="center">
          {payment.pix.qrCodeBase64 && (
            <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: '#fff' }}>
              <Box component="img" src={`data:image/png;base64,${payment.pix.qrCodeBase64}`} alt="QR Code PIX" sx={{ width: 220, height: 220, maxWidth: '100%', display: 'block' }} />
            </Box>
          )}
          <TextField
            label="PIX copia e cola"
            value={payment.pix.copiaECola}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={copyCode} aria-label="Copiar código"><ContentCopyRoundedIcon /></IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" size="large" fullWidth startIcon={<ContentCopyRoundedIcon />} onClick={copyCode}>
            {copied ? 'Código copiado!' : 'Copiar código PIX'}
          </Button>
          {payment.provider === 'simulado' && (
            <Alert severity="warning" sx={{ width: '100%' }} action={<Button color="inherit" size="small" onClick={simularPagamento}>Simular pagamento</Button>}>
              Ambiente de teste: este PIX é fictício.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Aguardando confirmação do pagamento...</Typography>
          </Stack>
          {payment.pix.expiresAt && (
            <Typography variant="caption" color="text.secondary">
              Válido até {new Date(payment.pix.expiresAt).toLocaleString('pt-BR')}
            </Typography>
          )}
        </Stack>
      );
    }

    if (generating) return <LoadingState label="Gerando o PIX..." minHeight={200} />;

    return (
      <Stack spacing={2}>
        {!payment.hasCpf && (
          <TextField
            label="CPF do pagador"
            value={cpf}
            onChange={(e) => setCpf(maskCpf(e.target.value))}
            inputProps={{ inputMode: 'numeric' }}
            helperText="Exigido pelo banco para emitir o PIX. Fica salvo no seu cadastro."
            fullWidth
          />
        )}
        <Button variant="contained" size="large" startIcon={<PixRoundedIcon />} onClick={() => generatePix(payment)} disabled={!payment.hasCpf && cpf.replace(/\D/g, '').length !== 11}>
          Gerar PIX
        </Button>
      </Stack>
    );
  };

  return (
    <>
      {payment && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              {payment.userName && <Typography color="text.secondary">Olá, {payment.userName}</Typography>}
              <Typography fontWeight={600}>{payment.description}</Typography>
            </Box>
            <PaymentStatusChip status={payment.status} />
          </Stack>
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '2.6rem', mt: 1 }}>
            {formatCurrency(payment.valorCobrado || payment.amount)}
          </Typography>
          {payment.modoTaxa === 'repassar' && Number(payment.taxa) > 0 && payment.status === 'pending' && (
            <Typography variant="body2" color="text.secondary">
              {formatCurrency(payment.amount)} + {formatCurrency(payment.taxa)} de taxa de processamento do PIX
            </Typography>
          )}
          <Divider sx={{ my: 2.5 }} />
        </>
      )}
      {error && payment && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {renderBody()}
    </>
  );
};

export default PixCheckout;
